import React, { useEffect, useMemo, useState } from "react";
import { Empty, Modal, Skeleton, Spin } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { alertNotification } from "../TopNotification/alertNotification";
import {
  redirectToStripePackageCheckout,
  redirectToBillingPortal,
  DEFAULT_BILLING_PORTAL_RETURN_PATH,
} from "../../shared/billing";
import styles from "./ManageSubscriptionModal.module.css";

const ENTERPRISE_PLAN_URL = "https://skillgigs.com/skillsradar-enterprise/";
const PROFESSIONAL_PLAN_URL = "https://skillgigs.com/skillsradar-professional/";

const normalizeText = (value = "") =>
  typeof value === "string"
    ? value.trim().replace(/^['"]+|['"]+$/g, "").toLowerCase()
    : "";

const openEnterprisePlanPage = () => {
  window.open(ENTERPRISE_PLAN_URL, "_blank", "noopener,noreferrer");
};

const getPricingModalPayload = (plansResponse) => {
  if (plansResponse?.data?.view === "pricing_modal") {
    return plansResponse.data;
  }

  if (plansResponse?.view === "pricing_modal") {
    return plansResponse;
  }

  return null;
};

const getCurrentPlanCode = (creditsBalance = {}) =>
  creditsBalance?.planCode ||
  creditsBalance?.productCode ||
  creditsBalance?.data?.planCode ||
  creditsBalance?.data?.productCode ||
  "";

const getCreditsBalancePayload = (creditsBalance = {}) =>
  creditsBalance?.data && typeof creditsBalance.data === "object"
    ? creditsBalance.data
    : creditsBalance || {};

const hasActiveSubscription = (creditsBalance = {}) => {
  const payload = getCreditsBalancePayload(creditsBalance);
  const normalizedStatus = normalizeText(payload?.status);
  const currentPeriodEnd = payload?.currentPeriodEnd;
  const hasActivePlanFlag = payload?.hasActivePlan;

  if (hasActivePlanFlag === false) {
    return false;
  }

  if (
    normalizedStatus &&
    ["expired", "cancelled", "canceled", "inactive", "past_due", "unpaid"].includes(
      normalizedStatus
    )
  ) {
    return false;
  }

  if (currentPeriodEnd) {
    const periodEndTime = new Date(currentPeriodEnd).getTime();

    if (!Number.isNaN(periodEndTime) && periodEndTime <= Date.now()) {
      return false;
    }
  }

  if (hasActivePlanFlag === true) {
    return true;
  }

  return normalizedStatus === "active" || normalizedStatus === "trialing";
};

const getVariantByCycle = (plan, billingCycle) => {
  if (!plan?.variants || typeof plan.variants !== "object") {
    return null;
  }

  if (billingCycle === "yearly") {
    return plan.variants.yearly || plan.variants.annual || plan.variants.monthly || null;
  }

  return plan.variants.monthly || plan.variants.yearly || null;
};

const isCurrentPlan = (plan, currentPlanCode, billingCycle, creditsBalance) => {
  const normalizedCurrentPlanCode = normalizeText(currentPlanCode);

  if (!normalizedCurrentPlanCode || !hasActiveSubscription(creditsBalance)) {
    return false;
  }

  const selectedVariant = getVariantByCycle(plan, billingCycle);
  const selectedVariantPlanCode = normalizeText(
    selectedVariant?.planCode || selectedVariant?.productCode
  );

  if (selectedVariantPlanCode && selectedVariantPlanCode === normalizedCurrentPlanCode) {
    return true;
  }

  const variantCodes = Object.values(plan?.variants || {})
    .map((variant) => normalizeText(variant?.planCode || variant?.productCode))
    .filter(Boolean);

  return variantCodes.includes(normalizedCurrentPlanCode);
};

const getDisplayPrice = (plan, billingCycle) => {
  const variant = getVariantByCycle(plan, billingCycle);
  const monthlyVariant = plan?.variants?.monthly || null;
  const formatCurrency = (value) => {
    if (!Number.isFinite(value) || value <= 0) {
      return "";
    }

    return `$${Number(value.toFixed(2)).toString()}`;
  };
  const monthlyPrice =
    Number(monthlyVariant?.priceUsd) ||
    Number(monthlyVariant?.price) ||
    Number(monthlyVariant?.amountUsd) ||
    Number(monthlyVariant?.amount) ||
    0;
  const yearlyMonthlyEquivalentPrice =
    Number(variant?.monthlyEquivalentPriceUsd) ||
    Number(variant?.monthlyEquivalentPrice) ||
    Number(variant?.monthlyEquivalentAmount) ||
    0;
  const yearlyPrice =
    Number(variant?.priceUsd) ||
    Number(variant?.price) ||
    Number(variant?.amountUsd) ||
    Number(variant?.amount) ||
    0;
  const discountPercent = Number(variant?.discountPercent) || 0;

  if (billingCycle === "yearly") {
    if (yearlyMonthlyEquivalentPrice > 0) {
      return formatCurrency(yearlyMonthlyEquivalentPrice);
    }

    if (
      yearlyPrice > 0 &&
      monthlyPrice > 0 &&
      discountPercent > 0 &&
      yearlyPrice === monthlyPrice
    ) {
      return formatCurrency(monthlyPrice * (1 - discountPercent / 100));
    }
  }

  const numericPrice =
    yearlyPrice ||
    Number(plan?.price?.amountUsd) ||
    Number(plan?.price?.amount) ||
    (billingCycle === "yearly"
      ? Number(plan?.annualPrice) ||
        Number(plan?.annuallyPrice) ||
        Number(plan?.yearlyPrice) ||
        Number(plan?.yearlyMonthlyPrice) ||
        Number(plan?.pricing?.annually) ||
        Number(plan?.pricing?.annual)
      : Number(plan?.monthlyPrice) ||
        Number(plan?.monthly) ||
        Number(plan?.monthlyAmount) ||
        Number(plan?.monthly_amount) ||
        Number(plan?.priceMonthly) ||
        Number(plan?.pricing?.monthly));

  return (
    (billingCycle === "yearly" &&
    yearlyPrice > 0 &&
    monthlyPrice > 0 &&
    discountPercent > 0 &&
    yearlyPrice === monthlyPrice
      ? ""
      : variant?.priceDisplay) ||
    plan?.price?.display ||
    (numericPrice > 0 ? `$${numericPrice}` : "") ||
    (variant?.priceUsd === 0 ? "$0" : "") ||
    (plan?.price?.amountUsd === 0 ? "$0" : "") ||
    "Custom"
  );
};

const getDisplaySubtitle = (plan, billingCycle) => {
  const variant = getVariantByCycle(plan, billingCycle);
  const rawSubtitle = variant?.subtitle || plan?.price?.subtitle || "";

  return rawSubtitle;
};

const getDisplayCta = (plan, billingCycle) => {
  const variant = getVariantByCycle(plan, billingCycle);

  return variant?.cta || plan?.cta || {};
};

const getDisplayProductCode = (plan, billingCycle) => {
  const variant = getVariantByCycle(plan, billingCycle);

  return (
    variant?.productCode ||
    variant?.planCode ||
    plan?.salesReference?.[billingCycle === "yearly" ? "yearlyProductCode" : "monthlyProductCode"] ||
    ""
  );
};

const isFreePlan = (plan) => normalizeText(plan?.tier) === "free";
const isStarterPlan = (plan) => normalizeText(plan?.tier) === "starter";
const isProfessionalPlan = (plan) => normalizeText(plan?.tier) === "professional";

export default function ManageSubscriptionModal({
  visible,
  onClose,
  plans,
  creditsBalance,
  allowCurrentPlanCheckout = false,
  loading = false,
}) {
  const pricingModalPayload = useMemo(() => getPricingModalPayload(plans), [plans]);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [submittingCode, setSubmittingCode] = useState("");

  useEffect(() => {
    if (!pricingModalPayload?.defaultBillingInterval) {
      return;
    }

    setBillingCycle(normalizeText(pricingModalPayload.defaultBillingInterval) || "monthly");
  }, [pricingModalPayload]);

  const currentPlanCode = getCurrentPlanCode(creditsBalance);
  const billingOptions = pricingModalPayload?.billingOptions || [];
  const planList = Array.isArray(pricingModalPayload?.plans)
    ? pricingModalPayload.plans
    : [];
  const currentPlanIsProfessional = planList.some(
    (plan) => isProfessionalPlan(plan) && isCurrentPlan(plan, currentPlanCode, billingCycle, creditsBalance)
  );

  const handlePlanAction = async (plan) => {
    const cta = getDisplayCta(plan, billingCycle);
    const productCode = cta?.productCode || getDisplayProductCode(plan, billingCycle);
    const action = normalizeText(cta?.action);
    const checkoutEnabled = cta?.checkoutEnabled === true;
    const planIsCurrent = isCurrentPlan(
      plan,
      currentPlanCode,
      billingCycle,
      creditsBalance
    );

    if (submittingCode || (planIsCurrent && !allowCurrentPlanCheckout)) {
      return;
    }

    const topLevelAction = normalizeText(plan?.cta?.action);

    if (action === "contact_sales" || topLevelAction === "contact_sales") {
      if (isProfessionalPlan(plan)) {
        window.open(PROFESSIONAL_PLAN_URL, "_blank", "noopener,noreferrer");
      } else {
        openEnterprisePlanPage();
      }
      return;
    }

    if (!checkoutEnabled && !isProfessionalPlan(plan)) {
      alertNotification("info", "Please contact us to switch to this plan.");
      return;
    }

    if (!productCode) {
      alertNotification("error", "This plan is not configured for checkout yet.");
      return;
    }

    const proceed = async () => {
      setSubmittingCode(productCode);
      try {
        await redirectToStripePackageCheckout({
          productCode,
          billingCycle,
        });
      } finally {
        setSubmittingCode("");
      }
    };

    if (hasActiveSubscription(creditsBalance)) {
      Modal.confirm({
        title: "Upgrade Plan",
        content:
          "Your current subscription will be cancelled when you upgrade. Are you sure you want to continue?",
        okText: "Yes",
        cancelText: "No",
        onOk: proceed,
      });
      return;
    }

    await proceed();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnClose
      width={1280}
      wrapClassName={styles.modalWrap}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            {pricingModalPayload?.title || "Choose Pricing Plan"}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close manage subscription modal"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className={styles.toggleWrap}>
          <div className={styles.toggleRow}>
            {billingOptions.map((option) => {
              const value = normalizeText(option?.value);
              const isActive = value === billingCycle;

              return (
                <button
                  key={value || option?.label}
                  type="button"
                  className={`${styles.toggleButton} ${
                    isActive ? styles.toggleButtonActive : ""
                  }`}
                  onClick={() => setBillingCycle(value || "monthly")}
                >
                  <span>{option?.label}</span>
                  {option?.badge ? (
                    <span className={styles.discountBadge}>{option.badge}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.cardsGrid}>
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <section key={`skeleton-${index}`} className={styles.planCard}>
                  <div className={styles.planCardBody}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                  </div>
                </section>
              ))
            : planList.map((plan) => {
                const cta = getDisplayCta(plan, billingCycle);
                const productCode = cta?.productCode || getDisplayProductCode(plan, billingCycle);
                const planCurrent = isCurrentPlan(
                  plan,
                  currentPlanCode,
                  billingCycle,
                  creditsBalance
                );
                const planActionEnabled =
                  allowCurrentPlanCheckout && planCurrent;
                const freePlan = isFreePlan(plan);
                const starterPlan = isStarterPlan(plan);
                const professionalPlan = isProfessionalPlan(plan);
                const isSubmitting =
                  Boolean(submittingCode) && submittingCode === productCode;
                const isDisabled =
                  isSubmitting ||
                  (!planActionEnabled && planCurrent) ||
                  freePlan ||
                  (currentPlanIsProfessional && starterPlan);
                const ctaLabel = freePlan
                  ? cta?.label || "Start Free"
                  : planActionEnabled
                    ? "Renew plan"
                    : planCurrent
                    ? "Current plan"
                    : starterPlan
                    ? cta?.label || "Upgrade Plan"
                    : "Talk To Sales";
                const highlighted = starterPlan ? true : professionalPlan ? false : plan?.highlighted === true;

                return (
                  <section
                    key={plan?.tier || plan?.displayName}
                    className={`${styles.planCard} ${
                      highlighted ? styles.planCardPopular : ""
                    } ${planCurrent ? styles.planCardCurrent : ""}`}
                  >
                    {planCurrent ? (
                      <div className={styles.currentPlanBadge}>Current Plan</div>
                    ) : highlighted ? (
                      <div className={styles.popularBadge}>
                        {plan?.highlightBadge || "Most Popular"}
                      </div>
                    ) : null}

                    <div className={styles.planCardBody}>
                      <div className={styles.planHeader}>
                        <h3 className={styles.planTitle}>{plan?.displayName}</h3>
                        {plan?.description ? (
                          <p className={styles.planDescription}>{plan.description}</p>
                        ) : null}
                        <div className={styles.badgeRow}>
                          <span className={styles.badgePlaceholder} aria-hidden="true" />
                        </div>
                        <div className={styles.planPrice}>
                          {getDisplayPrice(plan, billingCycle)}
                        </div>
                        <div className={styles.planSubtitle}>
                          {getDisplaySubtitle(plan, billingCycle) || "\u00A0"}
                        </div>
                      </div>

                      {planCurrent ? (
                        <button
                          type="button"
                          className={styles.cancelSubscriptionButton}
                          onClick={() =>
                            Modal.confirm({
                              title: "Cancel Subscription",
                              content:
                                "If you cancel your subscription, all remaining credits and current plan benefits will be lost. Are you sure you want to proceed?",
                              okText: "Proceed",
                              cancelText: "Keep Subscription",
                              onOk: () =>
                                redirectToBillingPortal({
                                  returnPath: DEFAULT_BILLING_PORTAL_RETURN_PATH,
                                }),
                            })
                          }
                        >
                          Cancel Subscription
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`${styles.actionButton} ${
                            professionalPlan ? styles.actionButtonPrimary : ""
                          } ${(freePlan || (currentPlanIsProfessional && starterPlan)) ? styles.actionButtonCurrent : ""}`}
                          onClick={() => handlePlanAction(plan)}
                          disabled={isDisabled}
                        >
                          {isSubmitting ? (
                            <span className={styles.buttonContent}>
                              <Spin size="small" />
                              <span>Loading...</span>
                            </span>
                          ) : (
                            <span className={styles.buttonContent}>
                              <span>{ctaLabel}</span>
                            </span>
                          )}
                        </button>
                      )}

                      <div className={styles.divider} />

                      <div className={styles.featuresTitle}>
                        {plan?.featureIntro || "Plan Includes"}
                      </div>

                      <div className={styles.featuresList}>
                        {(plan?.features || []).map((feature) => (
                          <div key={feature} className={styles.featureItem}>
                            <CheckOutlined className={styles.featureIcon} />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                );
              })}
        </div>

        {!loading && planList.length === 0 ? (
          <div className={styles.emptyState}>
            <Empty description="Pricing plans are not available right now" />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
